import React, { useEffect, useMemo, useState } from 'react'

import { Archive, ArchiveRestore, Ellipsis, Trash2 } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { PropertyType } from '~/client/graph/types.generated'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { CREATE_PROPERTY_RECORD_ID } from '~/client/src/desktop/views/ProjectSetUp/components/ProjectSetupResourceConfig/constants'
import { Button } from '~/client/src/shared/components/Button/Button'
import { PanelSections } from '~/client/src/shared/components/PanelSections/PanelSections'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/client/src/shared/components/shadcn-ui/tabs'
import { SidePanel } from '~/client/src/shared/components/SidePanel/SidePanel'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { LOCATION_KIND_PROPERTY } from '~/client/src/shared/resources/domain/locationKind'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { toast } from '~/client/src/shared/utils/toast'

import { makeUniquePropertyName, normalizePropertyName } from '../propertyName'
import { ResourceProperty } from '../ResourceProperties/types'
import ArchivePropertyDialog from './ArchivePropertyDialog'
import DeletePropertyDialog from './DeletePropertyDialog'
import FilePropertyConfig from './property-configs/FilePropertyConfig'
import GeneralPropertyConfig from './property-configs/GeneralPropertyConfig'
import HierarchyPropertyConfig from './property-configs/HierarchyPropertyConfig'
import SelectPropertyConfig from './property-configs/SelectPropertyConfig'
import TextPropertyConfig from './property-configs/TextPropertyConfig'
import { useHierarchyOptionUsage } from './property-configs/useHierarchyOptionUsage'
import {
  buildPropertyPreviewDraft,
  getPropertyConfigurationWithDefaults,
} from './propertyPreview'
import PropertyPreviewPanel from './PropertyPreviewPanel'

type PropertySidebarTab = 'details' | 'preview'

const PROPERTY_DETAILS_TAB: PropertySidebarTab = 'details'
const PROPERTY_PREVIEW_TAB: PropertySidebarTab = 'preview'

interface IProps {
  projectDateStore?: ProjectDateStore
  resourceTypeId: ResourceTypeId
  selectedRecordId: string
  properties: ResourceProperty[]
  onClose: () => void
  state?: DesktopInitialState
  resourceSchema: ResourceSchema
}

const PropertyEditSidebar: React.FC<IProps> = ({
  projectDateStore,
  resourceTypeId,
  selectedRecordId,
  properties,
  onClose,
  state,
  resourceSchema,
}) => {
  const isCreateMode = selectedRecordId === CREATE_PROPERTY_RECORD_ID

  const propertyData = useMemo(() => {
    if (!selectedRecordId || isCreateMode) return null
    return properties.find(p => p.name === selectedRecordId) || null
  }, [selectedRecordId, properties, isCreateMode])

  const [label, setLabel] = useState('')
  const [name, setName] = useState('')
  const [propertyType, setPropertyType] = useState<PropertyType>(
    PropertyType.Text,
  )
  const [configuration, setConfiguration] = useState<any>({})
  const [isNameManuallyEdited, setIsNameManuallyEdited] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false)
  const [activeTab, setActiveTab] =
    useState<PropertySidebarTab>(PROPERTY_DETAILS_TAB)
  useEffect(() => {
    setActiveTab(PROPERTY_DETAILS_TAB)
  }, [selectedRecordId])

  useEffect(() => {
    if (isCreateMode) {
      setLabel('')
      setName('')
      setPropertyType(PropertyType.Text)
      setConfiguration({})
      setIsNameManuallyEdited(false)
      return
    }

    if (propertyData) {
      setLabel(propertyData.displayName || '')
      setName(propertyData.name || '')
      setPropertyType(propertyData.propertyType as PropertyType)
      setConfiguration(propertyData.configuration || {})
    }
  }, [propertyData, isCreateMode])

  const activeProperty = useMemo<ResourceProperty>(() => {
    if (propertyData) {
      return propertyData
    }

    return {
      id: null,
      resourceTypeId: resourceTypeId,
      isExternal: false,
      isArchived: false,
      name,
      displayName: label,
      propertyType,
      position: properties.length,
      updatedAt: null,
      configuration: configuration || {},
    }
  }, [
    propertyData,
    resourceTypeId,
    name,
    label,
    propertyType,
    properties.length,
    configuration,
  ])

  const previewProperty = useMemo(
    () =>
      buildPropertyPreviewDraft({
        propertyData,
        label,
        name,
        propertyType,
        configuration,
        resourceTypeId,
        position: properties.length,
      }),
    [
      propertyData,
      label,
      name,
      propertyType,
      configuration,
      resourceTypeId,
      properties.length,
    ],
  )

  const hierarchyOptionUsage = useHierarchyOptionUsage(
    state?.activeProject?.id,
    propertyType === PropertyType.Hierarchy ? propertyData?.id : null,
  )

  // The location type list is fixed on the server: its kinds are the built-in
  // ones, and the property itself cannot be archived or deleted.
  const isFixedVocabulary =
    resourceTypeId === ResourceTypeId.LOCATIONS &&
    propertyData?.name === LOCATION_KIND_PROPERTY

  const normalizedName = normalizePropertyName(name)
  const existingPropertyNames = useMemo(
    () =>
      new Set(
        properties
          .map(property => normalizePropertyName(property.name || ''))
          .filter(Boolean),
      ),
    [properties],
  )
  const isDuplicateName =
    isCreateMode &&
    !!normalizedName &&
    existingPropertyNames.has(normalizedName)
  const isManualDuplicateName = isDuplicateName && isNameManuallyEdited
  const isNameValid = isSaving || !name.trim() || !isManualDuplicateName
  const canSaveCreate =
    !!label.trim() &&
    !!normalizedName &&
    !isManualDuplicateName &&
    !!resourceTypeId &&
    !!state?.activeProject?.id
  const isSaveEnabled = isCreateMode ? canSaveCreate : !isSaving

  const handleLabelChange = (value: string) => {
    setLabel(value)
    if (isCreateMode && !isNameManuallyEdited) {
      setName(normalizePropertyName(value))
    }
  }
  const handleNameChange = (value: string) => {
    setName(value)
    setIsNameManuallyEdited(true)
  }
  const handleLabelBlur = () => {
    if (!isCreateMode || isNameManuallyEdited) return
    setName(
      makeUniquePropertyName(
        normalizePropertyName(label),
        existingPropertyNames,
      ),
    )
  }

  const handleSave = async () => {
    const projectId = state?.activeProject?.id

    setIsSaving(true)
    try {
      if (isCreateMode) {
        if (!projectId || !canSaveCreate) {
          console.error('Missing required fields for property creation')
          return
        }

        const uniqueName = makeUniquePropertyName(
          normalizedName,
          existingPropertyNames,
        )

        const input = {
          name: uniqueName,
          displayName: label.trim(),
          propertyType,
          configuration: getPropertyConfigurationWithDefaults(
            propertyType,
            configuration,
          ),
        }
        await resourceSchema.createProperty(input)
      } else {
        if (!propertyData?.id || !projectId) {
          console.error('Missing required IDs for update')
          return
        }

        const input = { displayName: label.trim(), configuration }
        await resourceSchema.updateProperty(propertyData.id, input)
      }

      onClose()
    } catch (error) {
      console.error(
        isCreateMode
          ? 'Failed to create property:'
          : 'Failed to update property:',
        error,
      )
      // A refused save used to leave the sidebar open with nothing said.
      toast.error(
        error instanceof Error
          ? error.message
          : Localization.translator.failedToSave,
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleUnarchive = async () => {
    const projectId = state?.activeProject?.id
    if (!propertyData?.id || !projectId) return

    setIsSaving(true)
    try {
      await resourceSchema.unarchiveProperty(propertyData.id)
      onClose()
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : Localization.translator.failedToUnarchive,
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleted = () => {
    setIsDeleteDialogOpen(false)
    onClose()
  }

  const handleArchived = () => {
    setIsArchiveDialogOpen(false)
    onClose()
  }

  if (!isCreateMode && !propertyData) return null

  const showDangerZone =
    !isCreateMode &&
    propertyData &&
    !propertyData.isExternal &&
    !propertyData.isArchived &&
    !isFixedVocabulary
  const showUnarchive = !isCreateMode && propertyData && propertyData.isArchived

  return (
    <SidePanel zIndex={999} className="tw:w-[600px]">
      <PanelSections.Header
        title={
          isCreateMode
            ? Localization.translator.createNewProperty
            : Localization.translator.editProperty
        }
        onClose={onClose}
        actions={
          (showDangerZone || showUnarchive) && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={Localization.translator.actions}
                  />
                }
              >
                <Ellipsis />
              </DropdownMenuTrigger>
              <DropdownMenuContent side="bottom" align="end">
                {showUnarchive && (
                  <DropdownMenuItem onClick={handleUnarchive}>
                    <ArchiveRestore />
                    {Localization.translator.unArchive}
                  </DropdownMenuItem>
                )}
                {showDangerZone && (
                  <>
                    <DropdownMenuItem
                      onClick={() => setIsArchiveDialogOpen(true)}
                    >
                      <Archive />
                      {Localization.translator.archive}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => setIsDeleteDialogOpen(true)}
                    >
                      <Trash2 />
                      {Localization.translator.delete}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
      />

      <div className="tw:flex-1 tw:overflow-y-auto">
        <Tabs
          value={activeTab}
          onValueChange={value => setActiveTab(value as PropertySidebarTab)}
          className="tw:h-full"
        >
          <TabsList
            variant="line"
            className="tw:w-full tw:px-4 tw:bg-background"
          >
            <TabsTrigger value={PROPERTY_DETAILS_TAB}>
              {Localization.translator.propertyDetails}
            </TabsTrigger>
            <TabsTrigger value={PROPERTY_PREVIEW_TAB}>
              {Localization.translator.propertyPreview}
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value={PROPERTY_DETAILS_TAB}
            className="tw:m-0"
            keepMounted
          >
            <div className="tw:flex tw:flex-col">
              <GeneralPropertyConfig
                property={activeProperty}
                label={label}
                onLabelChange={handleLabelChange}
                onLabelBlur={isCreateMode ? handleLabelBlur : undefined}
                name={name}
                onNameChange={isCreateMode ? handleNameChange : undefined}
                type={propertyType}
                onTypeChange={isCreateMode ? setPropertyType : undefined}
                isNameValid={isCreateMode ? isNameValid : true}
                nameValidationMessage={
                  isCreateMode && isManualDuplicateName
                    ? Localization.translator.fieldShouldBeUnique
                    : ''
                }
                isCreateMode={isCreateMode}
                createdBy={propertyData?.createdBy}
              />

              {propertyType === PropertyType.Text && (
                <TextPropertyConfig
                  configuration={configuration}
                  onConfigChange={setConfiguration}
                />
              )}

              {propertyType === PropertyType.Select && (
                <SelectPropertyConfig
                  configuration={configuration}
                  onConfigChange={setConfiguration}
                />
              )}

              {propertyType === PropertyType.Hierarchy && (
                <HierarchyPropertyConfig
                  key={selectedRecordId || 'create'}
                  configuration={configuration}
                  isCreateMode={isCreateMode}
                  usedOptionValues={hierarchyOptionUsage.usedOptionValues}
                  isUsageUnknown={hierarchyOptionUsage.isLoading}
                  hasUsageError={hierarchyOptionUsage.hasError}
                  isFixedVocabulary={isFixedVocabulary}
                  onConfigChange={setConfiguration}
                />
              )}

              {propertyType === PropertyType.File && (
                <FilePropertyConfig
                  configuration={configuration}
                  onConfigChange={setConfiguration}
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value={PROPERTY_PREVIEW_TAB} className="tw:m-0">
            <PropertyPreviewPanel
              property={previewProperty}
              resourceTypeId={resourceTypeId}
              projectDateStore={projectDateStore}
              onConfigure={() => setActiveTab(PROPERTY_DETAILS_TAB)}
            />
          </TabsContent>
        </Tabs>
      </div>

      <div className="tw:flex tw:justify-end tw:gap-3 tw:p-6 tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-background">
        <Button variant="outline" onClick={onClose} disabled={isSaving}>
          {Localization.translator.cancel}
        </Button>
        <Button
          variant="default"
          onClick={handleSave}
          disabled={!isSaveEnabled || isSaving}
        >
          {isSaving && (
            <span data-icon="inline-start" className="tw:inline-flex">
              <Spinner size="sm" variant="white" />
            </span>
          )}
          {Localization.translator.saveChanges}
        </Button>
      </div>
      {propertyData?.id && state?.activeProject?.id && (
        <>
          <DeletePropertyDialog
            isOpen={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            propertyId={propertyData.id}
            projectId={state.activeProject.id}
            onDeleted={handleDeleted}
            resourceSchema={resourceSchema}
          />
          <ArchivePropertyDialog
            isOpen={isArchiveDialogOpen}
            onClose={() => setIsArchiveDialogOpen(false)}
            propertyId={propertyData.id}
            projectId={state.activeProject.id}
            onArchived={handleArchived}
            resourceSchema={resourceSchema}
          />
        </>
      )}
    </SidePanel>
  )
}

export default inject(
  'projectDateStore',
  'state',
)(observer(PropertyEditSidebar))
